import React, { FC, FormEvent, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { SizeMe, SizeMeProps } from 'react-sizeme';
import { debounce } from 'lodash';

import { CurrentStakeType } from './types';
import { FeatureFlag } from 'app/api/services/site-configs/types';
import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Scrollbar, ScrollbarPrimaryRef } from 'app/design-system/components/Scrollbar';
import SizeMeContent from 'app/design-system/components/SizeMeContent';
import { useTranslation } from 'app/lang/';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { openLoginDialog } from 'app/modules/Signing/actions';
import { selectIsLoggedIn } from 'app/redux/selectors/auth';

import { fetchPendingBigStakes } from './actions/bigStakes';
import { processStakes } from './actions/submission';
import BetSlipContent from './BetSlipContent';
import ActionButton from './components/ActionButton';
import BigStakesBar from './components/BigStakesBar';
import Conflicts from './components/Conflicts';
import Preferences from './components/Preferences';
import FooterStakeType from './components/StakeTypes';
import { ActionButtonState } from './constants';
import { PledgeStakesContext, usePledgeStakesContextValue } from './contexts/pledgeStakesSelect';
import { selectVisibleBigStakeInfo } from './selectors/bigStakes';
import { selectBets, selectLastAddedBet } from './selectors/entries';
import { selectCurrentStakeType } from './selectors/stakeTypes';
import { selectConflictState } from './selectors/state';
import { EntriesState } from './types/entries';
import actionButtonState from './utils/action-button-state';
import { getOffsetForScroll } from './utils/scrollbars';

import dictionary from './BetSlip.dictionary.json';

import styles from './BetSlip.module.scss';

// ---

type SizeMeHeight = SizeMeProps['size']['height'];

export type StoreProps = {
  loggedIn: boolean;
  hasEntries: boolean;
  stakeType: CurrentStakeType;
  hasConflict: boolean;
  lastAddedBet: EntriesState['lastAddedBet'];
  actionButtonState: ActionButtonState;
  visibleBigStakeInfo: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType;
};

type BetSlipProps = {
  headerHeight: number;
};

const SCROLL_DEBOUNCE_TIMEOUT = 700;

export const getOffsetForScrollWithDebounce = debounce(
  (callback) => callback(),
  SCROLL_DEBOUNCE_TIMEOUT,
);

const BetSlip: FC<BetSlipProps & DispatchProp & StoreProps> = ({
  loggedIn,
  hasEntries,
  stakeType,
  hasConflict,
  headerHeight,
  visibleBigStakeInfo,
  lastAddedBet,
  actionButtonState,
  dispatch,
}) => {
  const { t } = useTranslation();
  const allowOneClickBet = useFeatureFlag(FeatureFlag.ONE_CLICK_BET);
  const scrollbars = useRef<ScrollbarPrimaryRef | null>(null);

  const pledgeStakesContextData = usePledgeStakesContextValue();

  const [barsHeight, setBarsHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    if (loggedIn) {
      dispatch(fetchPendingBigStakes());
    }
  }, [loggedIn]);

  useEffect(() => {
    if (lastAddedBet !== null && scrollbars.current?.contentWrapperEl) {
      getOffsetForScrollWithDebounce(() => {
        if (!scrollbars.current || !lastAddedBet) {
          return null;
        }

        const scrollbarsView = scrollbars.current?.contentWrapperEl;

        const offset = getOffsetForScroll(lastAddedBet, scrollbars);

        if (offset === null) {
          return;
        }

        scrollbarsView.scrollTo({ top: offset, behavior: 'smooth' });
      });
    }
  }, [lastAddedBet]);

  const submitFormHandler = (event: FormEvent) => {
    event.preventDefault();

    switch (actionButtonState) {
      case ActionButtonState.READY:
        return dispatch(processStakes());

      case ActionButtonState.UNAUTHORIZED:
        return dispatch(openLoginDialog());
    }
  };

  const onTriggerSizeMeHeader = (height: SizeMeHeight) => {
    if (height !== null && height !== barsHeight) {
      setBarsHeight(height);
    }
  };

  const onTriggerSizeMeFooter = (height: SizeMeHeight) => {
    if (height !== null && height !== footerHeight) {
      setFooterHeight(height);
    }
  };

  const scrollbarsHeight = `calc(100vh - ${headerHeight}px - ${barsHeight}px - ${footerHeight}px)`;

  return (
    <>
      <SizeMe monitorHeight
              monitorWidth={false}>
        {({ size }: SizeMeProps) => (
          <SizeMeContent trigger={size.height}
                         onTrigger={onTriggerSizeMeHeader}>
            <div>
              <BigStakesBar />
              {(allowOneClickBet || hasEntries) && (
                <Preferences />
              )}

            </div>
          </SizeMeContent>
        )}
      </SizeMe>

      <PledgeStakesContext.Provider value={pledgeStakesContextData}>
        <form id="betSlip-form"
              data-test="betSlip-form"
              onSubmit={submitFormHandler}>
          <Scrollbar style={{ maxHeight: scrollbarsHeight }}
                     className={styles.scrollbarBetSlip}
                     ref={scrollbars}>
            <BetSlipContent />
          </Scrollbar>

          <SizeMe monitorHeight
                  monitorWidth={false}>
            {({ size }: SizeMeProps) => {
              if (!hasEntries) {
                return <></>;
              }

              return (
                <SizeMeContent trigger={size.height}
                               onTrigger={onTriggerSizeMeFooter}>
                  <div className={styles.footer}
                       data-test="betSlip-footer">
                    {visibleBigStakeInfo ? (
                      <p className={styles.bigStake}>{t(dictionary.bigStakeMessage)}</p>
                    ) : (
                      <>
                        {!hasConflict && (
                          <FooterStakeType stakeType={stakeType} />
                        )}
                        <Conflicts />
                      </>
                    )}

                    <ActionButton />
                  </div>
                </SizeMeContent>
              );
            }}
          </SizeMe>
        </form>
      </PledgeStakesContext.Provider>
    </>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const entriesSize = selectBets(state).size;

  return {
    loggedIn: selectIsLoggedIn(state),
    hasEntries: entriesSize > 0,
    stakeType: selectCurrentStakeType(state),
    hasConflict: selectConflictState(state) !== null,
    visibleBigStakeInfo: selectVisibleBigStakeInfo(state),
    lastAddedBet: selectLastAddedBet(state),
    actionButtonState: actionButtonState(state),
  };
};

export default connect(mapStateToProps)(BetSlip);
